import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// CONTEXT LÉTREHOZÁSA
// Ez az a "szekrény", ahol tároljuk az auth state-et
const AuthContext = createContext();

// AXIOS SETUP - HTTP kliens
// Ez az, amivel kommunikálunk a backend-del
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true,  // Cookies küldése is
});

// ============================================
// REQUEST INTERCEPTOR - Token hozzáadása
// ============================================
// Mit csinál: Minden API kéréshez hozzáadja az Access Token-t
// Miért: A backend az Authorization header-ben várja
api.interceptors.request.use(
  (config) => {
    // Lekérdezzük a localStorage-ből az access token-t
    const token = localStorage.getItem('accessToken');
    
    if (token) {
      // Ha van token: hozzáadjuk az Authorization header-hez
      config.headers.Authorization = `Bearer ${token}`;
      // "Authorization: Bearer eyJhbGc...xyz..."
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// RESPONSE INTERCEPTOR - Token Refresh
// ============================================
// Mit csinál: Ha 401-et kapunk (token lejárt), automatikus refresh
// Miről: Seamless user experience - felhasználó nem veszi észre!
// Ezeken az endpointokon a 401 várt válasz (rossz jelszó, rossz 2FA kód) —
// itt nem próbálunk token-refresh-t, különben a hibás login teljes
// oldal-újratöltést és átirányítást okozna
const NO_REFRESH_URLS = ['/auth/login', '/auth/refresh', '/auth/2fa/verify', '/auth/register'];

api.interceptors.response.use(
  (response) => response,  // OK válasz → direkt visszaadjuk
  async (error) => {
    // HIBA válasz feldolgozása
    const originalRequest = error.config;

    const skipRefresh = NO_REFRESH_URLS.some(u => originalRequest?.url?.includes(u));

    // 401 = Unauthorized (token lejárt vagy érvénytelen)
    if (error.response?.status === 401 && !originalRequest._retry && !skipRefresh) {
      // _retry = már próbáltunk-e refresh-t?
      // Ezt azért tesszük, hogy ne legyen végtelen loop!
      
      originalRequest._retry = true;  // Jelezzük, hogy próbálunk refresh-t

      try {
        // LÉPÉS 1: Refresh token lekérdezése a localStorage-ből
        const refreshToken = localStorage.getItem('refreshToken');
        
        // LÉPÉS 2: POST kérés a backend-nek
        // "Adj nekem új access token-t, itt a refresh token"
        const response = await api.post('/auth/refresh', { refreshToken });
        
        // LÉPÉS 3: Új token lekérdezése
        const { accessToken } = response.data;
        
        // LÉPÉS 4: Tokenek mentése a localStorage-be
        localStorage.setItem('accessToken', accessToken);
        
        // LÉPÉS 5: Axios default header frissítése
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        
        // LÉPÉS 6: Az eredeti kérés ÚJRA futtatása az új token-nel
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh sikertelen (pl. refresh token is lejárt)
        // → Kijelentkeztetjük a felhasználót
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        
        // Átirányítás a login oldalra
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH PROVIDER KOMPONENS
// ============================================
// Ez a komponens "burkolja" az egész app-ot
// Biztosítja, hogy a state elérhető legyen mindenhol
export const AuthProvider = ({ children }) => {
  // STATE VÁLTOZÓK
  const [user, setUser] = useState(null);           // Bejelentkezet felhasználó
  const [loading, setLoading] = useState(true);     // Betöltés közben?
  const [error, setError] = useState(null);         // Hiba üzenet

  // ============================================
  // 1. EFFECT: FELHASZNÁLÓ BETÖLTÉSE INDULÁSKOR
  // ============================================
  // Mit csinál: App induláskor ellenőrzi, hogy bejelentkezet-e
  // Miért: Ha F5 megnyomod, ne legyen kijelentkezve!
  useEffect(() => {
    const loadUserFromToken = async () => {
      // LÉPÉS 1: Token lekérdezése a localStorage-ből
      const token = localStorage.getItem('accessToken');
      
      if (token) {
        try {
          // LÉPÉS 2: Token dekódolása
          // (Az iat, exp, stb mezőkét vizsgáljuk)
          const decoded = jwtDecode(token);
          
          // LÉPÉS 3: Token lejárt-e?
          if (decoded.exp * 1000 < Date.now()) {
            // exp * 1000 = másodperc → milliszekundum
            // Date.now() = jelenlegi idő ms-ben
            // Ha a múltat = lejárt!
            
            // Token lejárt → próbálunk refresh-t
            const refreshToken = localStorage.getItem('refreshToken');
            const response = await api.post('/auth/refresh', { refreshToken });
            
            // Új token mentése
            localStorage.setItem('accessToken', response.data.accessToken);
          }

          // LÉPÉS 4: Felhasználó adatainak lekérdezése
          // GET /api/auth/me
          // Az axios interceptor automatikusan hozzáadja a tokent!
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch (err) {
          console.error('Failed to load user:', err);
          
          // Hiba: tokenek törlése
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setError('Session expired');
        }
      }
      
      // Betöltés véget ért
      setLoading(false);
    };

    loadUserFromToken();
  }, []);  // [] = csak induláskor futtasd le

  // ============================================
  // 2. REGISTER FÜGGVÉNY
  // ============================================
  // Mit csinál: Új felhasználó regisztrálása
  // Paraméterek: email, username, password, firstName, lastName
  const register = useCallback(async (email, username, password, firstName, lastName, inviteToken, preferredLanguage) => {
    try {
      setError(null);

      const response = await api.post('/auth/register', {
        email,
        username,
        password,
        first_name: firstName,
        last_name: lastName,
        ...(inviteToken ? { inviteToken } : {}),
        ...(preferredLanguage ? { preferred_language: preferredLanguage } : {}),
      });

      // Szétbontjuk a response-t
      const { accessToken, refreshToken, user } = response.data;
      
      // Tokenek mentése a localStorage-be
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      // Felhasználó state-et beállítjuk
      setUser(user);
      
      return { success: true, user };
    } catch (err) {
      // Hiba kezelése
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  // ============================================
  // 3. LOGIN FÜGGVÉNY
  // ============================================
  // Mit csinál: Felhasználó bejelentkeztetése
  // rememberMe: true → 30 napos refresh token, false → 1 napos
  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      setError(null);

      // POST kérés: POST /api/auth/login
      const response = await api.post('/auth/login', { email, password, rememberMe });

      // 2FA bekapcsolva → még nincs token, a kódot is be kell kérni
      if (response.data.requires2FA) {
        return { success: false, requires2FA: true, pendingToken: response.data.pendingToken };
      }

      const { accessToken, refreshToken, user } = response.data;

      // Tokenek mentése
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Felhasználó state beállítása
      setUser(user);

      return { success: true, user };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  // ============================================
  // 3/b. 2FA VERIFY — login 2. lépése
  // ============================================
  const verify2FA = useCallback(async (pendingToken, code) => {
    try {
      setError(null);

      const response = await api.post('/auth/2fa/verify', { pendingToken, code });

      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      setUser(user);

      return { success: true, user };
    } catch (err) {
      const message = err.response?.data?.error || '2FA verification failed';
      setError(message);
      return { success: false, error: message, expired: err.response?.status === 401 && message.includes('session') };
    }
  }, []);

  // ============================================
  // 4. LOGOUT FÜGGVÉNY
  // ============================================
  // Mit csinál: Felhasználó kijelentkeztetése
  // Paraméter: logoutAll = összes eszközről kijelentkezzen-e?
  const logout = useCallback(async (logoutAll = false) => {
    try {
      // Próbálunk server-side logout-ot csinálni
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/auth/logout', { refreshToken, logoutAll });
    } catch (err) {
      // Ha hiba: nem gond, úgyis töröljük az adatokat
      console.error('Logout error:', err);
    } finally {
      // MINDENKÉPPEN töröljük a localStorage-t
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setError(null);
    }
  }, []);

  // ============================================
  // CONTEXT VALUE - Amit a gyermek komponenseknek adunk
  // ============================================
  const value = {
    user,                   // A bejelentkezet felhasználó
    loading,                // Betöltés közben?
    error,                  // Hiba üzenet
    isAuthenticated: !!user, // Van-e bejelentkezet felhasználó?
    register,               // Register függvény
    login,                  // Login függvény
    verify2FA,              // 2FA kód ellenőrzése (login 2. lépés)
    logout,                 // Logout függvény
    api,                    // Axios instance (más komponensek is használhatják)
  };

  // ============================================
  // PROVIDER RENDER
  // ============================================
  // Burkolja az összes gyermek komponenst
  // value = amit szeretnénk megosztani
  // children = az app összes komponense
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// CUSTOM HOOK: useAuth
// ============================================
// Mit csinál: Egyszerű módszer az auth context lekérdezésére
// Használat: const { user, login, logout } = useAuth();
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    // Ha a Context.Provider-en kívül használják
    throw new Error('useAuth must be used within AuthProvider');
  }
  
  return context;
};